import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaCar, FaPlane, FaTicketAlt, FaWifi } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { rememberComparatorCategory, trackComparatorEvent } from '../../lib/comparatorAnalytics';

const categories = [
  { id: 'flights', icon: FaPlane, fr: 'Vols', en: 'Flights' },
  { id: 'cars', icon: FaCar, fr: 'Location d’auto', en: 'Car rentals' },
  { id: 'esim', icon: FaWifi, fr: 'eSIM', en: 'eSIM' },
  { id: 'experiences', icon: FaTicketAlt, fr: 'Expériences', en: 'Experiences' },
];

export default function ForfyCompareHomeSection() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');

  return (
    <section className="relative z-30 -mt-12 px-4 pb-16 md:-mt-16 md:pb-24">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          className="relative overflow-hidden rounded-[2.75rem] border border-cyan-300/20 bg-slate-950 shadow-[0_35px_100px_rgba(2,132,199,0.28)]"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(34,211,238,0.22),transparent_32%),radial-gradient(circle_at_90%_75%,rgba(59,130,246,0.24),transparent_34%),linear-gradient(135deg,#020617,#0f172a_58%,#082f49)]" />
          <div className="absolute -left-20 top-16 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative grid items-center gap-8 p-6 md:p-10 lg:grid-cols-[1.05fr_0.95fr] lg:p-14">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-cyan-200 backdrop-blur-xl">
                <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" /> Forfy Compare
              </div>
              <h2 className="mb-5 text-4xl font-black leading-[0.94] tracking-tighter text-white md:text-6xl">
                {isEn ? 'One trip. Every essential. One smart place.' : 'Un voyage. Tous les essentiels. Un seul endroit intelligent.'}
              </h2>
              <p className="mb-7 max-w-2xl text-base font-medium leading-relaxed text-slate-300 md:text-lg">
                {isEn
                  ? 'Forfy brings together flights, car rentals, eSIMs and events so you can plan faster — while keeping Forfeo local experiences within reach.'
                  : 'Forfy rassemble vols, location d’auto, eSIM et événements pour planifier plus vite — tout en gardant les expériences locales Forfeo à portée de main.'}
              </p>

              <div className="mb-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {categories.map(({ id, icon: Icon, fr, en }) => (
                  <button
                    key={id}
                    onClick={() => {
                      rememberComparatorCategory(id);
                      trackComparatorEvent('forfeo_comparator_home_category_click', { category: id });
                      navigate(`/comparateur-voyage?category=${id}`);
                    }}
                    className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.07] p-3 text-left text-white backdrop-blur-xl transition hover:-translate-y-1 hover:border-cyan-300/40 hover:bg-white/15"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-300/10 text-cyan-300 group-hover:bg-cyan-300 group-hover:text-slate-950">
                      <Icon />
                    </span>
                    <span className="text-xs font-black uppercase tracking-wider">{isEn ? en : fr}</span>
                  </button>
                ))}
              </div>

              <div className="mb-6 flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-wider text-slate-300">
                <span className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-2">
                  {isEn ? '4 travel essentials' : '4 essentiels voyage'}
                </span>
                <span className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-2">
                  {isEn ? 'Local Forfeo add-ons' : 'Expériences locales Forfeo'}
                </span>
                <span className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-2">
                  {isEn ? 'No extra affiliate cost' : 'Aucun frais affilié additionnel'}
                </span>
              </div>

              <button
                onClick={() => {
                  trackComparatorEvent('forfeo_comparator_home_category_click', { category: 'flights', placement: 'primary_cta' });
                  navigate('/comparateur-voyage');
                }}
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-cyan-300 to-blue-500 px-6 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-950 shadow-xl shadow-cyan-500/20 transition hover:-translate-y-1"
              >
                {isEn ? 'Compare with Forfy' : 'Comparer avec Forfy'} <FaArrowRight />
              </button>
            </div>

            <div className="relative mx-auto w-full max-w-[520px]">
              <div className="absolute inset-8 rounded-full bg-cyan-400/25 blur-3xl" />
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="relative overflow-hidden rounded-[2.75rem] border border-white/15 bg-white/10 p-3 shadow-2xl backdrop-blur-xl"
              >
                <div className="relative overflow-hidden rounded-[2.25rem] bg-gradient-to-b from-slate-900 to-cyan-950">
                  <video
                    src="/GifForfy.MP4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="h-[360px] w-full object-cover md:h-[470px]"
                    aria-label="Forfy, assistant comparateur Forfeo"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent p-6 pt-20">
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">
                      {isEn ? 'Your smart travel concierge' : 'Votre concierge voyage intelligent'}
                    </p>
                    <p className="mt-2 text-sm font-medium text-white/80">
                      {isEn ? 'Choose a category. I will guide the next step.' : 'Choisissez une catégorie. Je guide la prochaine étape.'}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
